import _ from "lodash";
import { PARENT, STAFF, STUDENT } from "Constants/stringConstants";
import { getFormattedUserName } from "Utils/GetFormattedUserName";

export const COHORT_TYPE = {
  MEMBERS: "members",
  CLASSES: "classes",
  GRADES: "grades",
};

const labelKeyMap = {
  [COHORT_TYPE.CLASSES]: "common:class_lowercase",
  [COHORT_TYPE.GRADES]: "common:grade_lowercase",
};

export const DEFAULT_METADATA = {
  getRightPanelHeader: ({ t, users }) => {
    const count = _.size(users);
    return count > 0
      ? t("common:selected_members_with_count", { count })
      : t("common:selected_members");
  },
  getRightPanelSubHeader: ({ t, users }) => {
    const countUsersByType = _.countBy(users, "type");
    return _.chain([STAFF, STUDENT, PARENT])
      .filter(type => countUsersByType[type])
      .map(type =>
        t(`announcement:count_${type}`, { count: countUsersByType[type] })
      )
      .value()
      .join(" • ");
  },
  showRightPanelClearAll: true,
  showStudentId: false,
  getSearchPlaceholder: ({ t }) => t("announcement:search_members"),
  renderCohortSubText: null,
  getEmptyCohortsTooltip: ({ cohortType, t }) => {
    const labelKey = labelKeyMap[cohortType];
    if (!labelKey) return null;
    return t("common:label_has_no_members", { label: t(labelKey) });
  },
};

export const DUAL_PANEL_VIEW_MODE = {
  MEMBERS: "MANAGE_MEMBERS",
  CLASSES: "MANAGE_CLASSES",
  GRADES: "MANAGE_GRADES",
};

export const HEADER_TYPE = {
  SCHOOL: "SCHOOL",
};

export const DUAL_PANEL_VIEW = {
  [DUAL_PANEL_VIEW_MODE.MEMBERS]: {
    key: DUAL_PANEL_VIEW_MODE.MEMBERS,
    labelKey: "common:members",
  },
  [DUAL_PANEL_VIEW_MODE.CLASSES]: {
    key: DUAL_PANEL_VIEW_MODE.CLASSES,
    cohortKey: COHORT_TYPE.CLASSES,
    labelKey: "common:class_plural",
  },
  [DUAL_PANEL_VIEW_MODE.GRADES]: {
    key: DUAL_PANEL_VIEW_MODE.GRADES,
    cohortKey: COHORT_TYPE.GRADES,
    labelKey: "common:grades",
  },
};

export const getSubText = ({
  user,
  t,
  defaultStudentNameSortPreference,
  showStudentId = false,
  studentId,
}) => {
  const userRole = _.get(user, "type");
  switch (userRole) {
    case STUDENT: {
      const grade = _.get(user, "yearGroup.grade.name");
      const curriculumLabel = _.get(user, "yearGroup.curriculumProgram.label");
      let subText;
      if (showStudentId && grade) {
        subText = `${studentId} • ${grade}`;
      } else if (showStudentId) {
        subText = `${studentId}`;
      } else if (grade) {
        subText = grade;
      }

      if (curriculumLabel) {
        subText = subText ? `${subText} • ${curriculumLabel}` : curriculumLabel;
      }
      return subText ?? t("common:student");
    }

    case STAFF: {
      const staffSubText = user.displayRole;
      const staffCurriculumLabels = _.map(user.curriculumPrograms, "label");
      if (!_.isEmpty(staffCurriculumLabels)) {
        const firstLabel = _.first(staffCurriculumLabels);
        const remainingCount = _.size(staffCurriculumLabels) - 1;
        const curriculumText =
          remainingCount > 0
            ? `${firstLabel} ${t("common:plus_more_with_count", {
                count: remainingCount,
              })}`
            : firstLabel;
        return staffSubText
          ? `${staffSubText} • ${curriculumText}`
          : curriculumText;
      }
      return staffSubText;
    }

    case PARENT: {
      const childrenCount = _.size(user.children);
      if (childrenCount === 1) {
        return t("common:family_member_of_label", {
          label: getFormattedUserName({
            user: _.first(user.children),
            type: STUDENT,
            orderBy: defaultStudentNameSortPreference,
          }),
        });
      } else if (childrenCount > 1) {
        return t("common:family_member_of_label_and_count", {
          label: getFormattedUserName({
            user: _.first(user.children),
            type: STUDENT,
            orderBy: defaultStudentNameSortPreference,
          }),
          count: childrenCount - 1,
        });
      }

      return t("common:family_of_count_student_other", {
        count: 0,
      });
    }
    default:
      break;
  }
};

// ----

export const getCoursesCohorts = _.memoize(
  (courses, skipNodeExtraction = false) => {
    const coursesCohorts = [];
    _.forEach(courses, course => {
      const {
        students = {},
        collaborator = {},
        familyMembers = {},
        id,
        title,
        grades = [],
      } = skipNodeExtraction ? course : _.get(course, "node", {});
      const studentsList = _.map(_.get(students, "edges", []), "node");
      const collaboratorList = _.map(_.get(collaborator, "edges", []), "node");
      const familyMembersList = _.map(
        _.get(familyMembers, "edges", []),
        "node"
      );

      coursesCohorts.push({
        id,
        title,
        type: STUDENT,
        userList: studentsList,
        grades,
      });
      coursesCohorts.push({
        id,
        title,
        type: STAFF,
        userList: collaboratorList,
        grades,
      });
      coursesCohorts.push({
        id,
        title,
        type: PARENT,
        userList: familyMembersList,
        grades,
      });
    });
    return coursesCohorts;
  }
);

export const getGradesCohorts = _.memoize(grades => {
  const gradeCohorts = [];
  _.forEach(grades, gradeItem => {
    const grade = _.get(gradeItem, "node", gradeItem);
    const {
      allStudents = [],
      allStaffs = [],
      allParents = [],
      id,
      name: title,
    } = grade;

    gradeCohorts.push({ id, title, type: STUDENT, userList: allStudents });
    gradeCohorts.push({ id, title, type: STAFF, userList: allStaffs });
    gradeCohorts.push({ id, title, type: PARENT, userList: allParents });
  });
  return gradeCohorts;
});
